import { html, LitElement } from "lit";
import { customElement, property } from 'lit/decorators.js';
import { localized, msg, str } from '@lit/localize';
import { when } from 'lit/directives/when.js';
import style from './table-nav-bar.scss';
import '../icon/usos-icon';

/**
 * Navigation bar for static paginated data tables.
 * Note that this component provides only navigation between static pages containing subsequent pages
 * of table, dynamic reloading is available only by providing JavaScript URL-syntax call in attributes or
 * by onClickFn property.
 *
 * @slot - extra content to show in navigation bar
 * @fires table-nav-bar:click - when user clicks on navigation buttons, with detail {button: 'first' | 'previous' | 'next' | 'last', originalEvent: Event}
 */
@customElement('table-nav-bar')
@localized()
export class TableNavBar extends LitElement {
    static override styles = style;

    /** Is current page the first in dataset? */
    @property({type: Boolean, attribute: 'is-first'}) isFirst = false;

    /** Is current page the last in dataset? */
    @property({type: Boolean, attribute: 'is-last'}) isLast = false;

    /** URL to view with first page of this table. */
    @property({attribute: 'first-page-url'}) firstPageUrl = '';

    /** URL to view with previous page of this table. */
    @property({attribute: 'previous-page-url'}) previousPageUrl = '';

    /** URL to view with next page of this table. */
    @property({attribute: 'next-page-url'}) nextPageUrl = '';

    /** URL to view with last page of this table. */
    @property({attribute: 'last-page-url'}) lastPageUrl = '';

    /** Current elements shown in table, in syntax 'N..M'. */
    @property({attribute: 'current-elements-number'}) currentElementsNumber = '';

    /** Total element count in table. */
    @property({attribute: 'elements-count', type: Number}) elementsCount: Optional<number> = null;

    /** Is this list empty? */
    @property({type: Boolean, attribute: 'empty'}) empty = false;

    /**
     * Whether to use buttons for navigation instead of links. This mode ignores link URLs,
     * but still honors `isFirst` and `isLast`.
     * Useful for navigation without page reloads, see Table toolbox.
     */
    @property({type: Boolean, attribute: 'use-buttons'}) useButtons = false;

    /**
     * Optional callback fired on click on navigation buttons.
     * If `false` is returned, link default action will be prevented.
     *
     * @deprecated This property is deprecated and should not be used in new code.
     * It is recommended to listen to `table-nav-bar:click` event instead, which provides
     * more information about the click and does not interfere with default link behavior.
     */
    @property({attribute: false})
    onClickFn: (evt: Event) => boolean | void = () => {};

    override render() {
        const [from, to] = this.currentElementsNumber.match(/^(\d+)\.\.(\d+)$/)?.slice(1) || ['0', '0'];
        const elementsCountLabel = msg(str`od ${from} do ${to}`);

        const elementsCountSpan = html`<span class="red" style="margin: 0 0.25rem;" aria-label="${elementsCountLabel}">${this.currentElementsNumber}</span>`;
        const elementsCountTotalSpan = html`<span class="red" style="margin: 0 0.25rem;">${this.elementsCount}</span>`;

        return html`
            <div id="nav-bar">
                ${this._renderFirstPageButton()}
                ${this._renderPreviousPageButton()}

                ${this.empty ? html`
                    <div id="count">${msg(str`Lista jest pusta`)}</div>
                    ` : html`
                    <div id="count">
                        ${when(this.elementsCount === null,
                            () => msg(html`Elementy ${elementsCountSpan}`),
                            () => msg(html`Elementy ${elementsCountSpan} z ${elementsCountTotalSpan}`))
                        }
                    </div>
                `}

                ${this._renderNextPageButton()}
                ${this._renderLastPageButton()}
                <slot></slot>
            </div>
        `;
    }

    private _renderFirstPageButton() {
        if (this.isFirst || this.useButtons) {
            return html`
                <button ?disabled=${this.isFirst} class='first-page' @click=${this._onClick}><usos-icon icon-name="first_page" .label=${msg(str`Pierwsza strona`)}></usos-icon></button>
            `;
        } else {
            return html`
                <a href="${this.firstPageUrl}" class='first-page' @click=${this._onClick}><usos-icon icon-name="first_page" .label=${msg(str`Pierwsza strona`)}></usos-icon></a>
            `;
        }
    }

    private _renderPreviousPageButton() {
        if (this.isFirst || this.useButtons) {
            return html`
                <button ?disabled=${this.isFirst} class='previous-page' @click=${this._onClick}><usos-icon icon-name="navigate_before" .label=${msg(str`Poprzednia strona`)}></usos-icon></button>
            `;
        } else {
            return html`
                <a href="${this.previousPageUrl}" class='previous-page' @click=${this._onClick}><usos-icon icon-name="navigate_before" .label=${msg(str`Poprzednia strona`)}></usos-icon></a>
            `;
        }
    }

    private _renderNextPageButton() {
        if (this.isLast || this.useButtons) {
            return html`
                <button ?disabled=${this.isLast} class='next-page' @click=${this._onClick}><usos-icon icon-name="navigate_next" .label=${msg(str`Następna strona`)}></usos-icon></button>
            `;
        } else {
            return html`
                <a href="${this.nextPageUrl}" class='next-page' @click=${this._onClick}><usos-icon icon-name="navigate_next" .label=${msg(str`Następna strona`)}></usos-icon></a>
            `;
        }
    }

    private _renderLastPageButton() {
        if (this.isLast || this.elementsCount === null || this.useButtons) {
            return html`
                <button ?disabled=${this.isLast || this.elementsCount === null} class='last-page' @click=${this._onClick}><usos-icon icon-name="last_page" .label=${msg(str`Ostatnia strona`)}></usos-icon></button>
            `;
        } else {
            return html`
                <a href="${this.lastPageUrl}" class='last-page' @click=${this._onClick}><usos-icon icon-name="last_page" .label=${msg(str`Ostatnia strona`)}></usos-icon></a>
            `;
        }
    }

    private _onClick(e: Event) {
        const target = e.currentTarget as HTMLElement;

        const buttonType = Array.from(target.classList).find(c => c.endsWith('-page'))
            ?.split('-')[0] as 'first' | 'previous' | 'next' | 'last';

        const customEvent = new CustomEvent('table-nav-bar:click', {
            detail: { button: buttonType, originalEvent: e },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(customEvent);
        // preserve original behavior
        if (this.onClickFn(e) === false) { e.preventDefault(); }
    }
}

declare global {
    interface HTMLElementTagNameMap {
      "table-nav-bar": TableNavBar,
    }
    interface HTMLElementEventMap {
        "table-nav-bar:click": CustomEvent<{button: 'first' | 'previous' | 'next' | 'last', originalEvent: Event}>,
    }
}
